import useCentralizedChatManager from '@/components/useCentralizedChatManager'
import ElectronIPC from '@/services/ElectronIPC'
import { ChatMessage } from '@/types/ChatContracts'
import { useTranslation } from 'react-i18next'
import { ChatboxTabTypes } from '../ChatRoom'
import { useCallback, useState } from 'react'
import Linkify from 'linkify-react'
import * as linkify from 'linkifyjs'
import MessageCellHeader from './MessageCellHeader'
import { MessageCellFile, MessageCellFileUploadError } from './MessageCellFile'
import { logger } from '@/services/Logger'
import { Button } from '@/UIComponents2/Button'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'
import { DialogBody } from '@/components/Dialog'
import { truncateString } from '@/utils'

const log = logger.createChildLogger({ name: 'ChatRoom' })

export default function ChatMessagesSection({
    chatManager,
    currentTab,
    messageHistory,
    msgLoadedBatchState,
    currentSelectedFiles,

    scrollContainerRef,
    downloadLockedFilesRef,
    doBlockDragModeRef,

    onCellSelectedHandler,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    currentTab: ChatboxTabTypes
    messageHistory: ChatMessage[]
    msgLoadedBatchState: { headIdx: number; loadedBatchesCt: number }
    currentSelectedFiles: { [key: string]: boolean }

    scrollContainerRef: React.RefObject<HTMLDivElement>
    downloadLockedFilesRef: React.MutableRefObject<Set<string>>
    doBlockDragModeRef: React.MutableRefObject<boolean>

    onCellSelectedHandler: (id: string, is: boolean) => void
    onDeleteSelectedCellsHandler: () => void
}) {
    log.verbose('ChatMessagesSection', 'rendering')

    return (
        <div id="chatbox-messages-container" className="relative overflow-hidden h-full flex-1 min-h-0 py-4">
            <div id="scroll-container" ref={scrollContainerRef} className="px-4 h-full overflow-y-auto">
                <MessageListContainer
                    chatManager={chatManager}
                    messageHistory={
                        currentTab === 'files'
                            ? messageHistory.filter((message) => message.contentType === 'file')
                            : messageHistory.slice(msgLoadedBatchState.headIdx)
                    }
                    currentTab={currentTab}
                    currentSelectedFiles={currentSelectedFiles}
                    onCellSelectedHandler={onCellSelectedHandler}
                    downloadLockedFilesRef={downloadLockedFilesRef}
                    onCellDragStart={() => (doBlockDragModeRef.current = true)}
                />
            </div>

            {/* TODO */}
            {/* <ScrollArrow direction="down" show={hasMoreText} onClick={() => scrollToBottom()} /> */}
        </div>
    )
}

function MessageListContainer({
    chatManager,
    messageHistory,
    currentTab,
    currentSelectedFiles,
    onCellSelectedHandler,
    downloadLockedFilesRef,
    onCellDragStart,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    messageHistory: ChatMessage[]
    currentTab: ChatboxTabTypes
    currentSelectedFiles: { [key: string]: boolean }
    onCellSelectedHandler: (id: string, is: boolean) => void
    downloadLockedFilesRef: React.MutableRefObject<Set<string>>
    onCellDragStart: () => void
}) {
    const { t } = useTranslation()

    const pendingMessagesCt = chatManager.messagePendingLookupRef.current?.size || 0

    if (messageHistory.length === 0 && pendingMessagesCt === 0) {
        return (
            <div className="flex h-full justify-center items-center">
                <div className="text-gray-400">{t('No messages')}</div>
            </div>
        )
    }

    // TODO: restore
    // const onContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    //     if (env.IS_ELECTRON) {
    //         event.preventDefault()
    //         const selection = window?.getSelection()?.toString() ?? ''
    //         if (selection.length > 0) {
    //             ElectronIPC.showContextMenu({
    //                 cut: false,
    //                 copy: true,
    //                 paste: false,
    //             })
    //         }
    //     }
    // }
    // <div onContextMenu={onContextMenu}>

    log.verbose('MessageListContainer', 'rendering')

    return (
        <MessageList
            chatManager={chatManager}
            messageHistory={messageHistory}
            currentTab={currentTab}
            currentSelectedFiles={currentSelectedFiles}
            downloadLockedFilesRef={downloadLockedFilesRef}
            onCellSelectedHandler={onCellSelectedHandler}
            onCellDragStart={onCellDragStart}
        />
    )
}

function MessageList({
    chatManager,
    messageHistory,
    currentTab,
    currentSelectedFiles,
    downloadLockedFilesRef,
    onCellSelectedHandler,
    onCellDragStart,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    messageHistory: ChatMessage[]
    currentTab: ChatboxTabTypes
    currentSelectedFiles: { [key: string]: boolean }
    downloadLockedFilesRef: React.MutableRefObject<Set<string>>
    onCellSelectedHandler: (id: string, is: boolean) => void
    onCellDragStart: () => void
}) {
    const { messagePendingLookupRef } = chatManager

    const containerClass = 'pt-2 pb-3'

    const messagePendingArray = Array.from(messagePendingLookupRef.current)
    const totalItems = [...messageHistory, ...messagePendingArray]

    // NOTE: keys are important for scrolls to behave correctly with message list virtualization
    const renderer = useCallback(
        (
            index: number,
            messageHistory: ChatMessage[],
            chatManager: ReturnType<typeof useCentralizedChatManager>,
            currentTab: ChatboxTabTypes,
            currentSelectedFiles: { [key: string]: boolean },
            downloadLockedFilesRef: React.MutableRefObject<Set<string>>,
            messagePendingArray: [string, ChatMessage][],
            onCellSelectedHandler: (id: string, is: boolean) => void
        ) => {
            // console.log(`*** MessageList renderer: index=${index} messageHistory=%O fileUploadProgressLookup=%O`,
            //     messageHistory, fileUploadProgressLookup)
            const { fileDownloadProgressLookup, fileUploadProgressLookup } = chatManager

            const chatmsg = messageHistory[index]

            if (index < messageHistory.length) {
                return (
                    <div key={chatmsg.id + '-container'} className={containerClass}>
                        {chatmsg.contentType === 'text' && (
                            <MessageCellText
                                key={chatmsg.id}
                                chatManager={chatManager}
                                chatmsg={chatmsg}
                                isInProgress={false}
                            />
                        )}
                        {chatmsg.contentType === 'file' && (
                            <MessageCellFile
                                key={chatmsg.id}
                                id={chatmsg.id}
                                chatManager={chatManager}
                                chatmsg={chatmsg}
                                currentTab={currentTab}
                                fileDownloadProgressLookup={fileDownloadProgressLookup}
                                isSelected={currentSelectedFiles[chatmsg.id] === true}
                                downloadLockedFilesRef={downloadLockedFilesRef}
                                onCellSelected={(is) => onCellSelectedHandler(chatmsg.id, is)}
                                onCellDragStart={onCellDragStart}
                            />
                        )}
                    </div>
                )
            } else {
                index = index - messageHistory.length

                if (messagePendingArray[index]) {
                    const [key, chatmsg] = messagePendingArray[index]
                    const fileProgressData = fileUploadProgressLookup.get(key)

                    if (chatmsg.contentType === 'text') {
                        return (
                            <div key={`${key}-container`} className={containerClass}>
                                <MessageCellText
                                    key={key}
                                    chatManager={chatManager}
                                    chatmsg={chatmsg}
                                    isInProgress={true}
                                />
                            </div>
                        )
                    } else {
                        if (fileProgressData?.error) {
                            return (
                                <div key={`${key}-container`} className={containerClass}>
                                    <MessageCellFileUploadError
                                        key={key}
                                        id={key}
                                        chatManager={chatManager}
                                        chatmsg={chatmsg}
                                        fileProgressData={fileProgressData}
                                    />
                                </div>
                            )
                        } else {
                            return (
                                <div key={`${key}-container`} className={containerClass}>
                                    <MessageCellFile
                                        key={key}
                                        id={key}
                                        chatManager={chatManager}
                                        chatmsg={chatmsg}
                                        currentTab={currentTab}
                                        fileDownloadProgressLookup={fileDownloadProgressLookup}
                                        fileUploadProgress={fileProgressData}
                                        isSelected={currentSelectedFiles[chatmsg.id] === true}
                                        downloadLockedFilesRef={downloadLockedFilesRef}
                                        onCellSelected={(is) => onCellSelectedHandler(chatmsg.id, is)}
                                        onCellDragStart={onCellDragStart}
                                    />
                                </div>
                            )
                        }
                    }
                } else {
                    return <div style={{ height: 1 }}></div>
                }
            }
        },
        []
    )

    return (
        <>
            {totalItems.map((_, i) => {
                return renderer(
                    i,
                    messageHistory,
                    chatManager,
                    currentTab,
                    currentSelectedFiles,
                    downloadLockedFilesRef,
                    messagePendingArray,
                    onCellSelectedHandler
                )
            })}
        </>
    )
}

function MessageCellText({
    chatManager,
    chatmsg,
    isInProgress,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    chatmsg: ChatMessage
    isInProgress: boolean
}) {
    const chat_truncate_threshold = 500

    const [showAllDialog, setShowAllDialog] = useState(false)

    // resolve chatmsg.senderUrn to a username/name.
    const userDisplayName = chatManager.resolveMessageUsername(chatmsg.senderUrn) || chatmsg.senderUrn
    const isMe = chatManager.userUrn === chatmsg.senderUrn

    if (chatmsg.contentType !== 'text') {
        // breaks contract: contentType must be 'text'.
        return <></>
    }

    const str: string = chatmsg.body
    const links = linkify.find(str)

    const truncatedStr = truncateString(str, chat_truncate_threshold)
    const isTruncated = truncatedStr !== str

    const Container = ({ children }: { children: React.ReactNode }) => {
        const containerClass = `flex flex-col break-words ${isMe ? 'items-end' : 'items-start'}`
        const textContainerClass = `flex flex-col w-4/5 ${isMe ? 'items-end' : 'items-start'}`
        const textClass = isInProgress ? 'text-gray-500' : 'text-white'

        return (
            <div className={containerClass}>
                <div className={textContainerClass}>
                    <MessageCellHeader isMe={isMe} userDisplayName={userDisplayName} timestamp={chatmsg.timestamp} />
                    <div className={`${textClass} w-fit max-w-full whitespace-pre-wrap`}>{children}</div>
                    {isTruncated && (
                        <div className="flex w-full justify-center mt-1">
                            <Button variant="link" className="text-xs" onClick={() => setShowAllDialog(true)}>
                                Show all
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    if (links.length === 0 || isTruncated) {
        if (showAllDialog) {
            return (
                <>
                    <CrossPlatformDialog onDefaultClose={() => setShowAllDialog(false)}>
                        <DialogBody>
                            <div className="w-full whitespace-pre-line break-all">{str}</div>
                        </DialogBody>
                    </CrossPlatformDialog>
                    <Container>{truncatedStr}</Container>
                </>
            )
        } else {
            return <Container>{truncatedStr}</Container>
        }
    } else {
        const renderLink = ({ attributes, content }: { attributes: any; content: any }) => {
            return (
                <a
                    className="text-muse-yellow hover:underline"
                    href={'#'}
                    onClick={(e) => {
                        e.preventDefault()
                        ElectronIPC.openLinkInBrowser(attributes.href)
                    }}
                >
                    {content}
                </a>
            )
        }

        return (
            <Container>
                <Linkify options={{ render: renderLink }}>{str}</Linkify>
            </Container>
        )
    }
}

{
    /* TODO: add file selection */
}
{
    /* {Object.keys(currentSelectedFiles).length > 0 && (
                <div
                    id="fab-container"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'absolute',
                        right: 0,
                        bottom: 0,
                        margin: '1rem',
                    }}
                >
                    <div title="Deselect all">
                        <Fab
                            color="primary"
                            size="small"
                            style={{ zIndex: 10000, backgroundColor: 'orange', marginBottom: '0.5rem' }}
                            onClick={() => set_currentSelectedFiles({})}
                        >
                            <RemoveIcon />
                        </Fab>
                    </div>
                    <div title="Select all files">
                        <Fab
                            color="primary"
                            size="small"
                            style={{ zIndex: 10000, backgroundColor: 'orange', marginBottom: '0.5rem' }}
                            onClick={() => {
                                const ids: { [key: string]: boolean } = {}
                                messageHistory
                                    .filter((message) => message.contentType === 'file')
                                    .forEach((message) => (ids[message.id] = true))
                                set_currentSelectedFiles(ids)
                            }}
                        >
                            <PlaylistAddIcon />
                        </Fab>
                    </div>
                    <div title="Delete (cannot be undone)">
                        <Fab
                            color="primary"
                            size="small"
                            style={{ zIndex: 10000, backgroundColor: 'red' }}
                            onClick={onDeleteSelectedCellsHandler}
                        >
                            <DeleteIcon />
                        </Fab>
                    </div>
                </div>
            )} */
}
